// src/components/admin/ListadoCategorias.js
import React, { useEffect, useState } from 'react';
import { ref, listAll, uploadBytes, deleteObject } from 'firebase/storage';
import { storage, auth } from '../../firebase/config';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { 
  FaTrash, 
  FaEdit, 
  FaPlus, 
  FaSearch, 
  FaDumbbell, 
  FaArrowLeft, 
  FaHome, 
  FaSignOutAlt,
  FaFolderOpen
} from 'react-icons/fa';

const APP_PRIMARY = '#b03060';

const DISPLAY_CATEGORIES = {
  abdominales: 'Abdominales',
  biceps: 'Bíceps',
  espalda: 'Espalda',
  hombro: 'Hombro',
  pecho: 'Pecho',
  piernas: 'Piernas',
  calentamiento: 'Anexos',
  'plancha entrenamiento core': 'Plancha Entrenamiento Core',
  triceps: 'Tríceps'
};

function normalizar(texto = '') {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Función auxiliar recursiva para eliminar de forma segura
 * todos los archivos reales contenidos dentro de una carpeta o sus subcarpetas.
 */
async function eliminarDirectorioRecursivo(directorioRef) {
  const listado = await listAll(directorioRef);

  // 1. Borrar todos los archivos del nivel actual
  const borradoArchivos = listado.items.map((itemRef) => deleteObject(itemRef));

  // 2. Si existen subcarpetas (prefixes), descender recursivamente
  const borradoSubdirectorios = listado.prefixes.map((subRef) =>
    eliminarDirectorioRecursivo(subRef)
  );

  await Promise.all([...borradoArchivos, ...borradoSubdirectorios]);
}

const ListadoCategorias = () => {
  const navigate = useNavigate();

  const [categorias, setCategorias] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState(null);

  const [modalNuevaVisible, setModalNuevaVisible] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState('');

  const [modalEliminarVisible, setModalEliminarVisible] = useState(false);
  const [categoriaAEliminar, setCategoriaAEliminar] = useState('');

  const [modalEditarVisible, setModalEditarVisible] = useState(false);
  const [categoriaAEditar, setCategoriaAEditar] = useState('');
  const [nuevoNombreEdicion, setNuevoNombreEdicion] = useState('');

  useEffect(() => {
    fetchCategorias();
  }, []);

  const fetchCategorias = async () => {
    try {
      const mftRef = ref(storage, 'mft/');
      const listado = await listAll(mftRef);
      const nombres = listado.prefixes.map((subRef) =>
        subRef.name.replace(/\/$/, '')
      );
      setCategorias(nombres);
    } catch (err) {
      console.error(err);
      setError('No se pudieron cargar las categorías.');
    } finally {
      setCargando(false);
    }
  };

  const crearCategoria = async () => {
    if (!nuevaCategoria.trim() || procesando) return;

    setProcesando(true);
    const folderName = `${Date.now()}_${nuevaCategoria
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')}/`;

    const dummyRef = ref(storage, `mft/${folderName}placeholder.txt`);

    try {
      await uploadBytes(dummyRef, new Uint8Array());
      setCategorias((prev) => [...prev, folderName.replace(/\/$/, '')]);
      setModalNuevaVisible(false);
      setNuevaCategoria('');
    } catch (err) {
      console.error('Error al crear categoría:', err);
    } finally {
      setProcesando(false);
    }
  };

  const eliminarCategoria = async () => {
    if (!categoriaAEliminar || procesando) return;

    setProcesando(true);
    const folderRef = ref(storage, `mft/${categoriaAEliminar}/`);
    try {
      await eliminarDirectorioRecursivo(folderRef);

      setCategorias((prev) =>
        prev.filter((c) => c !== categoriaAEliminar)
      );
    } catch (err) {
      console.error('Error al eliminar categoría en Storage:', err);
    } finally {
      setProcesando(false);
      setModalEliminarVisible(false);
      setCategoriaAEliminar('');
    }
  };

  const editarCategoria = async () => {
    if (!nuevoNombreEdicion.trim() || procesando) return;

    setProcesando(true);
    const nuevaRuta = `${Date.now()}_${nuevoNombreEdicion
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')}/`;

    const origenRef = ref(storage, `mft/${categoriaAEditar}/`);
    const dummyRef = ref(storage, `mft/${nuevaRuta}placeholder.txt`);

    try {
      await uploadBytes(dummyRef, new Uint8Array());
      await eliminarDirectorioRecursivo(origenRef);

      setCategorias((prev) =>
        prev.map((c) =>
          c === categoriaAEditar ? nuevaRuta.replace(/\/$/, '') : c
        )
      );
    } catch (err) {
      console.error('Error al editar categoría:', err);
    } finally {
      setProcesando(false);
      setModalEditarVisible(false);
      setCategoriaAEditar('');
      setNuevoNombreEdicion('');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (err) {
      alert('Error al cerrar sesión: ' + err.message);
    }
  };

  const obtenerNombreParaMostrar = (cat) => {
    const claveSinFecha = cat
      .replace(/^\d+(?:_\d+)*_/, '')
      .replace(/_/g, ' ');

    return (
      DISPLAY_CATEGORIES[claveSinFecha] ||
      (claveSinFecha.charAt(0).toUpperCase() + claveSinFecha.slice(1))
    );
  };

  const categoriasFiltradas = categorias.filter((cat) => {
    const nombre = obtenerNombreParaMostrar(cat);
    return normalizar(nombre).includes(normalizar(busqueda.trim()));
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Encabezado superior */}
        <div style={styles.headerRow}>
          <button onClick={() => navigate('/admin')} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver al panel
          </button>
          <span style={styles.headerBadge}>Gestión de Rutinas</span>
        </div>

        <div style={styles.titleSection}>
          <h2 style={styles.title}>Categorías y Planes</h2>
          <p style={styles.subtitle}>
            Administra los grupos de entrenamiento y sus rutinas de ejercicios
          </p>
        </div>

        {/* Barra superior de controles: Buscador y Botón Crear */}
        <div style={styles.controlsRow}>
          <div style={styles.searchWrapper}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar categoría o plan..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={styles.search}
            />
          </div>

          <button
            onClick={() => setModalNuevaVisible(true)}
            style={styles.nuevaCategoriaButton}
          >
            <FaPlus size={12} /> Nueva Categoría
          </button>
        </div>

        {cargando ? (
          <div style={styles.centerBox}>
            <p style={{ margin: 0, color: '#64748b' }}>Cargando categorías…</p>
          </div>
        ) : error ? (
          <div style={styles.centerBox}>
            <p style={{ margin: 0, color: '#dc2626' }}>{error}</p>
          </div>
        ) : (
          <div style={styles.listContainer}>
            {categoriasFiltradas.length === 0 ? (
              <div style={styles.emptyBox}>
                <FaFolderOpen size={30} color="#cbd5e1" style={{ marginBottom: 8 }} />
                <p style={{ color: '#94a3b8', margin: 0, fontSize: 14 }}>
                  No se encontraron categorías coincidentes
                </p>
              </div>
            ) : (
              categoriasFiltradas.map((cat) => (
                <div key={cat} style={styles.categoryCard}>
                  <button
                    onClick={() => navigate(`/admin/rutinas/${cat}`)}
                    style={styles.categoryMainButton}
                  >
                    <div style={styles.categoryIconCircle}>
                      <FaDumbbell size={15} color={APP_PRIMARY} />
                    </div>
                    <span style={styles.categoryNameText}>
                      {obtenerNombreParaMostrar(cat)}
                    </span>
                  </button>

                  <div style={styles.actionButtonsWrapper}>
                    <button
                      onClick={() => {
                        setCategoriaAEditar(cat);
                        setNuevoNombreEdicion(obtenerNombreParaMostrar(cat));
                        setModalEditarVisible(true);
                      }}
                      style={styles.editBtn}
                      title="Editar nombre"
                    >
                      <FaEdit size={13} />
                    </button>

                    <button
                      onClick={() => {
                        setCategoriaAEliminar(cat);
                        setModalEliminarVisible(true);
                      }}
                      style={styles.deleteBtn}
                      title="Eliminar categoría"
                    >
                      <FaTrash size={13} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal Crear */}
      <Modal
        isOpen={modalNuevaVisible}
        onRequestClose={() => !procesando && setModalNuevaVisible(false)}
        style={modalOverlayStyles}
        ariaHideApp={false}
      >
        <div style={styles.modalContent}>
          <h3 style={styles.modalTitle}>Nueva Categoría</h3>
          <p style={styles.modalSubtitle}>Ingresa el nombre para el nuevo grupo o plan</p>
          <input
            placeholder="Ej: Plan avanzado mujer - casa"
            value={nuevaCategoria}
            onChange={(e) => setNuevaCategoria(e.target.value)}
            style={styles.modalInput}
            disabled={procesando}
            autoFocus
          />
          <div style={styles.modalButtons}>
            <button 
              onClick={() => setModalNuevaVisible(false)} 
              style={styles.cancelBtn}
              disabled={procesando}
            >
              Cancelar
            </button>
            <button 
              onClick={crearCategoria} 
              style={{
                ...styles.confirmBtn,
                opacity: procesando ? 0.7 : 1,
                cursor: procesando ? 'not-allowed' : 'pointer'
              }}
              disabled={procesando}
            >
              {procesando ? 'Creando...' : 'Crear Categoría'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        isOpen={modalEliminarVisible}
        onRequestClose={() => !procesando && setModalEliminarVisible(false)}
        style={modalOverlayStyles}
        ariaHideApp={false}
      >
        <div style={styles.modalContent}>
          <h3 style={{ ...styles.modalTitle, color: '#dc2626' }}>¿Eliminar categoría?</h3>
          <p style={styles.modalSubtitle}>
            Esta acción borrará permanentemente la carpeta en el almacenamiento y todos los archivos que contiene.
          </p>
          <div style={styles.modalButtons}>
            <button 
              onClick={() => setModalEliminarVisible(false)} 
              style={styles.cancelBtn}
              disabled={procesando}
            >
              Cancelar
            </button>
            <button 
              onClick={eliminarCategoria} 
              style={{
                ...styles.deleteConfirmBtn,
                opacity: procesando ? 0.7 : 1,
                cursor: procesando ? 'not-allowed' : 'pointer'
              }}
              disabled={procesando}
            >
              {procesando ? 'Eliminando...' : 'Sí, eliminar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Editar */}
      <Modal
        isOpen={modalEditarVisible}
        onRequestClose={() => !procesando && setModalEditarVisible(false)}
        style={modalOverlayStyles}
        ariaHideApp={false}
      >
        <div style={styles.modalContent}>
          <h3 style={styles.modalTitle}>Editar Nombre</h3>
          <p style={styles.modalSubtitle}>Modifica la denominación asignada a la categoría</p>
          <input
            value={nuevoNombreEdicion}
            onChange={(e) => setNuevoNombreEdicion(e.target.value)}
            style={styles.modalInput}
            disabled={procesando}
            autoFocus
          />
          <div style={styles.modalButtons}>
            <button 
              onClick={() => setModalEditarVisible(false)} 
              style={styles.cancelBtn}
              disabled={procesando}
            >
              Cancelar
            </button>
            <button 
              onClick={editarCategoria} 
              style={{
                ...styles.confirmBtn,
                opacity: procesando ? 0.7 : 1,
                cursor: procesando ? 'not-allowed' : 'pointer'
              }}
              disabled={procesando}
            >
              {procesando ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dock inferior flotante */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={navDockStyles.dockButton}
          >
            <div style={navDockStyles.iconWrapper}>
              <FaHome size={14} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{ ...navDockStyles.dockButton, color: '#dc2626' }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={13} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListadoCategorias;

/* =========================== ESTILOS =========================== */
const styles = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px 120px 16px',
    boxSizing: 'border-box',
    fontFamily: "'Poppins', sans-serif"
  },
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 22,
    padding: '32px 28px',
    maxWidth: 820,
    margin: '0 auto',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
    border: '1px solid #f1f5f9'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  backButton: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '8px 16px',
    borderRadius: 50,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease'
  },
  headerBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  titleSection: {
    marginBottom: 24
  },
  title: {
    color: '#1e293b',
    fontSize: 26,
    fontWeight: '700',
    margin: '0 0 6px 0',
    letterSpacing: '-0.3px'
  },
  subtitle: {
    color: '#64748b',
    fontSize: 14,
    margin: 0
  },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20
  },
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 280px'
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    color: '#94a3b8',
    fontSize: 13
  },
  search: {
    width: '100%',
    padding: '10px 16px 10px 38px',
    borderRadius: 50,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    color: '#1e293b',
    background: '#ffffff',
    boxSizing: 'border-box'
  },
  nuevaCategoriaButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 18px',
    background: APP_PRIMARY,
    color: '#ffffff',
    border: 'none',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    boxShadow: '0 3px 10px rgba(176, 48, 96, 0.25)',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  },
  centerBox: {
    padding: '40px 20px',
    textAlign: 'center'
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '36px 20px',
    background: '#f8fafc',
    borderRadius: 14,
    border: '1px dashed #e2e8f0'
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  categoryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#ffffff',
    border: '1.5px solid #f1f5f9',
    borderRadius: 16,
    padding: '6px 10px 6px 14px',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
    transition: 'all 0.2s ease'
  },
  categoryMainButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    outline: 'none'
  },
  categoryIconCircle: {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  categoryNameText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b'
  },
  actionButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  editBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  deleteBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  modalContent: {
    padding: '6px'
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 6px 0'
  },
  modalSubtitle: {
    fontSize: 13,
    color: '#64748b',
    margin: '0 0 16px 0',
    lineHeight: 1.4
  },
  modalInput: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 20
  },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10
  },
  cancelBtn: {
    padding: '9px 16px',
    borderRadius: 50,
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#64748b',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer'
  },
  confirmBtn: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: APP_PRIMARY,
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(176, 48, 96, 0.25)'
  },
  deleteConfirmBtn: {
    padding: '9px 18px',
    borderRadius: 50,
    border: 'none',
    background: '#dc2626',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(220, 38, 38, 0.25)'
  }
};

const modalOverlayStyles = {
  overlay: {
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16
  },
  content: {
    position: 'static',
    width: '100%',
    maxWidth: 420,
    background: '#ffffff',
    borderRadius: 20,
    padding: 24,
    border: '1px solid #f1f5f9',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    overflow: 'visible'
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};
